// src/components/crearPaciente/TransferListContenidos.js

import React, { useCallback, useMemo } from "react";
import {
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Box,
  Divider,
} from "@mui/material";
import PropTypes from "prop-types";
import { ModernTextField } from "./styledComponents";

const TransferListContenidos = ({
  allContenidos = [],
  selectedContenidos = {},
  setSelectedContenidos = () => {},
  searchTerm,
  setSearchTerm,
}) => {
  // Normaliza texto eliminando acentos y mayúsculas para búsquedas flexibles
  const normalizeText = useCallback((text = "") => {
    return text
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase();
  }, []);

  const filteredContenidos = useMemo(
    () =>
      allContenidos.filter((contenido) =>
        normalizeText(contenido.nombre).includes(normalizeText(searchTerm))
      ),
    [allContenidos, normalizeText, searchTerm]
  );

  const sortedContenidos = useMemo(
    () =>
      filteredContenidos.slice().sort((a, b) => {
        const aSelected = selectedContenidos[a.nombre] ? 1 : 0;
        const bSelected = selectedContenidos[b.nombre] ? 1 : 0;
        return bSelected - aSelected;
      }),
    [filteredContenidos, selectedContenidos]
  );

  const handleToggle = useCallback(
    (nombre) => {
      setSelectedContenidos({
        ...selectedContenidos,
        [nombre]: !selectedContenidos[nombre],
      });
    },
    [selectedContenidos, setSelectedContenidos]
  );

  return (
    <Box>
      {/* Buscador de Contenidos */}
      <ModernTextField
        fullWidth
        placeholder="Buscar contenidos..."
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />
      <Divider sx={{ mb: 2 }} />
      {/* Lista de Contenidos */}
      <List
        sx={{
          maxHeight: "300px",
          overflowY: "auto",
        }}
      >
        {sortedContenidos.map((contenido) => {
          const nombre = contenido.nombre;
          const isSelected = Boolean(selectedContenidos[nombre]);
          return (
            <ListItemButton
              key={nombre}
              onClick={() => handleToggle(nombre)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                border: isSelected
                  ? "1px solid rgba(79,116,254,0.3)"
                  : "1px solid transparent",
              }}
            >
              <Checkbox
                checked={isSelected}
                tabIndex={-1}
                disableRipple
                color="primary"
              />
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: isSelected ? 600 : 400 }}
                  >
                    {nombre}
                  </Typography>
                }
              />
            </ListItemButton>
          );
        })}
        {sortedContenidos.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ padding: 2 }}
          >
            No hay contenidos disponibles.
          </Typography>
        )}
      </List>
    </Box>
  );
};

TransferListContenidos.propTypes = {
  allContenidos: PropTypes.array.isRequired,
  selectedContenidos: PropTypes.object.isRequired,
  setSelectedContenidos: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
};

export default TransferListContenidos;
